import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { initTheme } from "@/lib/themes";

// Apply persisted theme before React paints
initTheme();
import Login from "@/pages/Login";
import CampusDirectory from "@/pages/CampusDirectory";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import ChangePassword from "@/pages/ChangePassword";
import BulkOnboard from "@/pages/BulkOnboard";
import Landing from "@/pages/Landing";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import MfaSetup from "@/pages/MfaSetup";
import Tenants from "@/pages/Tenants";
import TenantDetail from "@/pages/TenantDetail";
import Institutions from "@/pages/Institutions";
import AcademicSetup from "@/pages/AcademicSetup";
import BulkImport from "@/pages/BulkImport";
import Students from "@/pages/Students";
import StudentProfile from "@/pages/StudentProfile";
import CampusDetail from "@/pages/CampusDetail";
import DepartmentDashboard from "@/pages/DepartmentDashboard";
import FinanceDashboard from "@/pages/FinanceDashboard";
import Parents from "@/pages/Parents";
import Staff from "@/pages/Staff";
import UsersAdmin from "@/pages/UsersAdmin";
import RoleTemplates from "@/pages/RoleTemplates";
import Admissions from "@/pages/Admissions";
import ApplicationDetail from "@/pages/ApplicationDetail";
import PublicApply from "@/pages/PublicApply";
import Attendance from "@/pages/Attendance";
import AttendanceQrCheckin from "@/pages/AttendanceQrCheckin";
import StaffAttendance from "@/pages/StaffAttendance";
import StaffLeaves from "@/pages/StaffLeaves";
import Regularisations from "@/pages/Regularisations";
import Fees from "@/pages/Fees";
import Notices from "@/pages/Notices";
import CreateNotice from "@/pages/CreateNotice";
import Homework from "@/pages/Homework";
import Library from "@/pages/Library";
import Curriculum from "@/pages/Curriculum";
import CurriculumAssessment from "@/pages/CurriculumAssessment";
import Syllabus from "@/pages/Syllabus";
import ExamStructure from "@/pages/ExamStructure";
import ExamSessions from "@/pages/ExamSessions";
import Infrastructure from "@/pages/Infrastructure";
import ClassesSections from "@/pages/ClassesSections";
import Campuses from "@/pages/Campuses";
import { Campus360, Staff360, Student360 } from "@/pages/Profile360Pages";
import Gradebook from "@/pages/Gradebook";
import Timetable from "@/pages/Timetable";
import AuthCallback from "@/pages/AuthCallback";
import { PlatformScopeProvider } from "@/context/PlatformScope";

function HashCallbackGate({ children }) {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PlatformScopeProvider>
          <HashCallbackGate>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/directory" element={<CampusDirectory />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/terms" element={<TermsOfService />} />
            <Route path="/login" element={<Login />} />
            {/* Path-based tenant routing — edukampus.com/p/{slug} lands on
                the branded login for that campus. Works in parallel with
                subdomain-based routing (stpeters-kengeri.edukampus.com). */}
            <Route path="/p/:slug" element={<Login />} />
            <Route path="/p/:slug/login" element={<Login />} />
            <Route path="/p/:slug/forgot-password" element={<ForgotPassword />} />
            <Route path="/p/:slug/reset-password" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mfa"
              element={
                <ProtectedRoute>
                  <MfaSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenants"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin"]}>
                  <Tenants />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenants/:id"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin"]}>
                  <TenantDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/institutions"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin"]}>
                  <Institutions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/academic"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin", "principal"]}>
                  <AcademicSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bulk-import"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin"]}>
                  <BulkImport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bulk-onboard"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <BulkOnboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/students"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin", "principal", "hod", "teacher", "accountant", "librarian"]}>
                  <Students />
                </ProtectedRoute>
              }
            />
            <Route
              path="/students/:id"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin", "principal", "hod", "teacher", "accountant", "librarian"]}>
                  <StudentProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parents"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin", "principal", "hod", "teacher", "accountant"]}>
                  <Parents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin", "principal", "hod", "accountant"]}>
                  <Staff />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin"]}>
                  <UsersAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/role-templates"
              element={
                <ProtectedRoute>
                  <RoleTemplates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admissions"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin", "principal", "hod", "accountant"]}>
                  <Admissions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admissions/:id"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin", "principal", "hod", "accountant"]}>
                  <ApplicationDetail />
                </ProtectedRoute>
              }
            />
            <Route path="/apply/:slug" element={<PublicApply />} />
            <Route
              path="/attendance"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin", "principal", "hod", "teacher"]}>
                  <Attendance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/regularisations"
              element={
                <ProtectedRoute>
                  <Regularisations />
                </ProtectedRoute>
              }
            />
            <Route path="/attendance/qr/:token" element={<AttendanceQrCheckin />} />
            <Route
              path="/staff-attendance"
              element={
                <ProtectedRoute>
                  <StaffAttendance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leaves"
              element={
                <ProtectedRoute>
                  <StaffLeaves />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fees"
              element={
                <ProtectedRoute>
                  <Fees />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notices"
              element={
                <ProtectedRoute>
                  <Notices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notices/create"
              element={
                <ProtectedRoute>
                  <CreateNotice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/homework"
              element={
                <ProtectedRoute>
                  <Homework />
                </ProtectedRoute>
              }
            />
            <Route
              path="/library"
              element={
                <ProtectedRoute>
                  <Library />
                </ProtectedRoute>
              }
            />
            <Route
              path="/syllabus"
              element={
                <ProtectedRoute>
                  <Syllabus />
                </ProtectedRoute>
              }
            />
            <Route
              path="/curriculum"
              element={
                <ProtectedRoute>
                  <Curriculum />
                </ProtectedRoute>
              }
            />
            <Route
              path="/curriculum/:structureId/assessment/:nodeId"
              element={
                <ProtectedRoute>
                  <CurriculumAssessment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/exam-structure"
              element={
                <ProtectedRoute>
                  <ExamStructure />
                </ProtectedRoute>
              }
            />
            <Route
              path="/exam-sessions"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "principal", "campus_admin", "hod", "coordinator", "exam_controller"]}>
                  <ExamSessions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/infrastructure"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "principal", "campus_admin", "infrastructure_manager", "it_admin"]}>
                  <Infrastructure />
                </ProtectedRoute>
              }
            />
            <Route
              path="/classes-sections"
              element={
                <ProtectedRoute>
                  <ClassesSections />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campuses"
              element={
                <ProtectedRoute>
                  <Campuses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/gradebook"
              element={
                <ProtectedRoute>
                  <Gradebook />
                </ProtectedRoute>
              }
            />
            <Route
              path="/timetable"
              element={
                <ProtectedRoute>
                  <Timetable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campus-360"
              element={
                <ProtectedRoute>
                  <Campus360 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff-360"
              element={
                <ProtectedRoute>
                  <Staff360 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/student-360"
              element={
                <ProtectedRoute>
                  <Student360 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/department-360"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin"]}>
                  <DepartmentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance-360"
              element={
                <ProtectedRoute roles={["super_admin", "inst_admin", "campus_admin", "finance", "accountant"]}>
                  <FinanceDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campuses/:id"
              element={
                <ProtectedRoute>
                  <CampusDetail />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </HashCallbackGate>
          <Toaster richColors closeButton position="bottom-right" />
          </PlatformScopeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
